import { useEffect, useState } from "react";

import { apiFetch } from "@/lib/http/client";
import { parseTranscript, type TranscriptData } from "@/lib/recordings/parse-transcript";

// ── Types ─────────────────────────────────────────────────────────────────────

export type UseTranscriptResult = {
  /** Parsed transcript or null while idle / failed / unavailable. */
  data: TranscriptData | null;
  /** True while the fetch is in flight. */
  loading: boolean;
  /** Non-null when the fetch failed. */
  error: string | null;
};

// ── Hook ──────────────────────────────────────────────────────────────────────

/**
 * Fetches the transcript blob for a recording via the platform-agnostic
 * `apiFetch` HTTP client and parses it through `parseTranscript`. Works on
 * web, desktop, and mobile — the underlying transport (`HttpAdapter`) handles
 * the auth + base URL per platform.
 *
 * - Returns the idle state `{ data: null, loading: false, error: null }`
 *   immediately when `recordingId` is null (no fetch).
 * - In-flight fetches are cancelled via the standard `let cancelled = false`
 *   pattern when the recordingId changes or the component unmounts.
 */
export function useTranscript(recordingId: string | null): UseTranscriptResult {
  const [data, setData] = useState<TranscriptData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!recordingId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    apiFetch(`/recordings/${recordingId}/transcript`)
      .then((raw) => {
        if (cancelled) return;
        // The API returns the raw JSONB blob directly (not wrapped in
        // `{ transcript: ... }` like the Tauri command did).
        setData(parseTranscript(raw));
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setData(null);
        setError(
          err instanceof Error ? err.message : String(err) || "fetch failed",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [recordingId]);

  return { data, loading, error };
}
